import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fetchGameAnalysis } from '../api/gameAnalysis';
import WinProbabilityChart from './analysis/WinProbabilityChart';
import RunsScoredChart from './analysis/RunsScoredChart';
import KeyPlays from './analysis/KeyPlays';
import TeamStatComparison from './analysis/TeamStatComparison';
import PitchingMatchup from './analysis/PitchingMatchup';
import MomentumTracker from './analysis/MomentumTracker';
import GameHighlights from './analysis/GameHighlights';
import PerformanceTrends from './analysis/PerformanceTrends';
import PlayerPerformanceTrends from './analysis/PlayerPerformanceTrends';

const { FiX, FiBarChart2, FiZap, FiTrendingUp, FiList, FiActivity, FiStar, FiUsers } = FiIcons;

const TABS = [
  { id: 'highlights', label: 'Highlights', icon: FiStar },
  { id: 'winprob', label: 'Win Prob', icon: FiTrendingUp },
  { id: 'momentum', label: 'Momentum', icon: FiActivity },
  { id: 'trends', label: 'Trends', icon: FiBarChart2 },
  { id: 'players', label: 'Players', icon: FiUsers },
  { id: 'runs', label: 'Runs/Inn', icon: FiBarChart2 },
  { id: 'stats', label: 'Team Stats', icon: FiList },
  { id: 'plays', label: 'Key Plays', icon: FiZap },
];

const PostGameAnalysis = ({ game, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('highlights');

  useEffect(() => {
    if (!game?.gamePk) return;
    setLoading(true);
    fetchGameAnalysis(game.gamePk)
      .then(setAnalysis)
      .catch(() => setError('Could not load game analysis.'))
      .finally(() => setLoading(false));
  }, [game?.gamePk]);

  const awayAbbr = game?.teams?.away?.team?.abbreviation || 'AWY';
  const homeAbbr = game?.teams?.home?.team?.abbreviation || 'HME';
  const awayScore = game?.teams?.away?.score ?? game?.linescore?.teams?.away?.runs ?? '--';
  const homeScore = game?.teams?.home?.score ?? game?.linescore?.teams?.home?.runs ?? '--';
  const innings = analysis?.linescoreData?.innings || [];
  const allPlays = analysis?.playByPlay?.allPlays || [];
  const boxScore = analysis?.boxScore || game?.boxscore || null;
  const isLive = game?.status?.abstractGameState === 'Live';

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-2xl bg-[#1e3a29] border-4 border-[#d8ceb3] shadow-2xl z-10 max-h-[94vh] flex flex-col"
          initial={{ scale: 0.92, y: 24 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92, y: 24 }}
          transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        >
          {/* Header */}
          <div className="bg-[#386646] border-b-2 border-[#658d71] p-3 sm:p-4 flex items-center justify-between shrink-0">
            <div>
              <p className="text-[9px] tracking-widest uppercase text-[#a9c9b1] font-bold">
                {isLive ? 'Live Analysis' : 'Post-Game Analysis'}
              </p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-base sm:text-xl font-black text-white tracking-widest">{awayAbbr}</span>
                <span className="text-xl sm:text-3xl font-black text-[#d8ceb3] tabular-nums">
                  {awayScore} – {homeScore}
                </span>
                <span className="text-base sm:text-xl font-black text-white tracking-widest">{homeAbbr}</span>
                {isLive && (
                  <span className="flex items-center gap-1 text-[9px] text-[#c4333e] font-black uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#c4333e] animate-pulse" />
                    Live
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 border border-[#658d71] text-[#d8ceb3] hover:bg-[#658d71] transition-colors"
            >
              <SafeIcon icon={FiX} size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b-2 border-[#658d71] shrink-0 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-2 sm:px-3 py-2.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-colors whitespace-nowrap border-r border-[#658d71] last:border-0 ${
                  activeTab === tab.id
                    ? 'bg-[#386646] text-[#d8ceb3]'
                    : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'
                }`}
              >
                <SafeIcon icon={tab.icon} size={10} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split('/')[0]}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-5">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
                <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading analysis...</span>
              </div>
            )}
            {error && <p className="text-center text-[#e6a8a8] text-sm py-8">{error}</p>}
            {!loading && !error && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="flex flex-col gap-4"
                >
                  {activeTab === 'highlights' && (
                    <>
                      <div>
                        <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                          Game Highlights & Notable Moments
                        </p>
                        <GameHighlights allPlays={allPlays} boxScore={boxScore} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                      </div>
                      <PitchingMatchup boxScore={boxScore} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                    </>
                  )}

                  {activeTab === 'winprob' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Estimated Home Win Probability by Play
                      </p>
                      <WinProbabilityChart allPlays={allPlays} homeTeamName={homeAbbr} awayTeamName={awayAbbr} />
                    </div>
                  )}

                  {activeTab === 'momentum' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Scoring Momentum & Lead Changes
                      </p>
                      <MomentumTracker allPlays={allPlays} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                    </div>
                  )}

                  {activeTab === 'trends' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Team Performance Trends
                      </p>
                      <PerformanceTrends allPlays={allPlays} innings={innings} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                    </div>
                  )}

                  {activeTab === 'players' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Player Performance Trends
                      </p>
                      <PlayerPerformanceTrends
                        allPlays={allPlays}
                        boxScore={boxScore}
                        awayAbbr={awayAbbr}
                        homeAbbr={homeAbbr}
                      />
                    </div>
                  )}

                  {activeTab === 'runs' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Runs Scored per Inning
                      </p>
                      <RunsScoredChart innings={innings} homeAbbr={homeAbbr} awayAbbr={awayAbbr} />
                    </div>
                  )}

                  {activeTab === 'stats' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Team Batting & Pitching Comparison
                      </p>
                      <TeamStatComparison boxScore={boxScore} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                    </div>
                  )}

                  {activeTab === 'plays' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Key Plays & Scoring Moments
                      </p>
                      <KeyPlays allPlays={allPlays} />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PostGameAnalysis;